/**
 * Create Evaluation Page
 *
 * Form to design and configure a new assessment (quiz/exam).
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router';
import {
  ArrowLeft,
  Save,
  Loader2,
  ClipboardCheck,
  Sparkles,
  Search,
  X,
  BookOpen,
  Check,
  Info,
} from 'lucide-react';
import { Button, Card, Input } from '@lms/ui';
import { useCreateEvaluation } from '../hooks/useEvaluations';
import { usePrograms } from '../hooks/usePrograms';
import type { EvaluationType } from '@lms/shared/types';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';

export function CreateEvaluation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const stateProgramId = (location.state as { programId?: string })?.programId;
  const stateMaterialId = (location.state as { materialId?: string })?.materialId;
  const stateFlowId = (location.state as { flowId?: string })?.flowId;
  const stateLevelId = (location.state as { levelId?: string })?.levelId;
  const { mutate: create, isPending } = useCreateEvaluation();
  const { data: programsData, isLoading: isLoadingPrograms } = usePrograms();
  const programItems = Array.isArray(programsData?.items) ? programsData.items : [];

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    programId: stateProgramId || '',
    materialId: stateMaterialId || '',
    flowId: stateFlowId || '',
    levelId: stateLevelId || '',
    type: 'QUIZ' as EvaluationType,
    passingScore: 70,
    maxAttempts: 1,
    timeLimit: 0,
    numberOfQuestions: '',
  });

  const [programSearchQuery, setProgramSearchQuery] = useState('');
  const [isProgramSuggestOpen, setIsProgramSuggestOpen] = useState(false);
  const programSuggestRef = useRef<HTMLDivElement>(null);

  // Click outside listener for program auto-suggest dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (programSuggestRef.current && !programSuggestRef.current.contains(e.target as Node)) {
        setIsProgramSuggestOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedProgram = useMemo(() => {
    return programItems.find(p => p.id === formData.programId);
  }, [programItems, formData.programId]);

  const filteredPrograms = useMemo(() => {
    const q = programSearchQuery.trim().toLowerCase();
    if (!q) return programItems;
    return programItems.filter(p => {
      const matchName = p.name?.toLowerCase().includes(q);
      const matchCode = p.code?.toLowerCase().includes(q);
      const matchDesc = p.description?.toLowerCase().includes(q);
      return matchName || matchCode || matchDesc;
    });
  }, [programItems, programSearchQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Ensure numeric values are numbers and handle optional IDs
    const payload = {
      ...formData,
      passingScore: Number(formData.passingScore),
      maxAttempts: Number(formData.maxAttempts),
      timeLimit: formData.timeLimit ? Number(formData.timeLimit) : undefined,
      numberOfQuestions: formData.numberOfQuestions
        ? Number.parseInt(formData.numberOfQuestions, 10)
        : undefined,
      programId: formData.programId || undefined,
      materialId: formData.materialId || undefined,
      flowId: formData.flowId || undefined,
      levelId: formData.levelId || undefined,
    };

    create(payload, {
      onSuccess: data => {
        // Navigate to detail page to add questions
        void navigate(`/evaluations/${data.id}`);
      },
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'type') {
        next.maxAttempts = value === 'QUIZ' ? 3 : 1;
      }
      return next;
    });
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs font-body text-on-surface-variant">
        <Link to="/evaluations" className="hover:text-primary transition-colors">
          <Bilingual {...copy.createEvaluation.breadcrumbTitle} />
        </Link>
        <span>/</span>
        <span className="text-on-surface">
          <Bilingual {...copy.createEvaluation.breadcrumbCreate} />
        </span>
      </nav>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/evaluations"
            className="p-2 hover:bg-surface-container rounded-full transition-colors text-on-surface-variant"
          >
            <ArrowLeft size={20} />
          </Link>
          <div className="space-y-1">
            <h1 className="text-2xl font-headline font-bold text-on-surface leading-none m-0">
              <Bilingual {...copy.createEvaluation.title} />
            </h1>
            <p className="text-xs font-body text-on-surface-variant opacity-60">
              <Bilingual {...copy.createEvaluation.subtitle} />
            </p>
          </div>
        </div>
      </div>

      <Card className="max-w-2xl border-none shadow-level1 overflow-hidden">
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.createEvaluation.labelTitle} />
              </label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder={
                  language === 'ar'
                    ? copy.createEvaluation.placeholderTitle.ar
                    : copy.createEvaluation.placeholderTitle.id
                }
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
                required
              />
            </div>

            {/* Context banner when creating from flow/level */}
            {(stateFlowId || stateLevelId || stateMaterialId) && (
              <div className="bg-primary/5 rounded-2xl p-4 border border-primary/10 flex items-center gap-3">
                <Sparkles size={18} className="text-primary shrink-0" />
                <div className="text-[10px] font-bold text-on-surface leading-relaxed">
                  {stateFlowId && <Bilingual {...copy.createEvaluation.attachWeekly} />}
                  {stateLevelId && !stateFlowId && (
                    <Bilingual {...copy.createEvaluation.attachMonthly} />
                  )}
                  {stateMaterialId && <Bilingual {...copy.createEvaluation.attachMaterial} />}
                </div>
              </div>
            )}

            {/* Program Selection with Auto-Suggest Combobox */}
            <div className="space-y-2" ref={programSuggestRef}>
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest flex items-center justify-between">
                <span>
                  <Bilingual {...copy.createEvaluation.labelProgram} />
                </span>
                {selectedProgram && (
                  <span className="text-[10px] text-primary font-bold lowercase">
                    id: {selectedProgram.id.slice(0, 8)}...
                  </span>
                )}
              </label>

              {/* Display Selected Program Card if chosen */}
              {selectedProgram ? (
                <div className="p-4 rounded-2xl bg-surface-container-low border border-primary/20 flex items-center justify-between gap-3 animate-fadeIn">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <BookOpen size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-headline font-bold text-sm text-on-surface truncate">
                          {selectedProgram.name}
                        </span>
                        {selectedProgram.code && (
                          <span className="px-2 py-0.5 rounded-md bg-primary/15 text-primary font-mono text-[10px] font-bold shrink-0">
                            {selectedProgram.code}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-on-surface-variant m-0 truncate">
                        {selectedProgram.description || selectedProgram.code || 'Program Kurikulum'}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, programId: '' }));
                      setProgramSearchQuery('');
                      setIsProgramSuggestOpen(true);
                    }}
                    className="px-3 py-1.5 rounded-xl bg-surface-container-high hover:bg-surface-container text-on-surface text-xs font-label font-bold uppercase tracking-wider shrink-0 transition-colors"
                  >
                    <Bilingual {...copy.createEvaluation.changeProgram} />
                  </button>
                </div>
              ) : (
                /* Search & Auto-Suggest Input */
                <div className="relative">
                  <div className="relative">
                    <Search
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-40 rtl:left-auto rtl:right-4"
                    />
                    <input
                      type="text"
                      placeholder={t(copy.createEvaluation.searchProgramPlaceholder)}
                      value={programSearchQuery}
                      onFocus={() => setIsProgramSuggestOpen(true)}
                      onChange={e => {
                        setProgramSearchQuery(e.target.value);
                        setIsProgramSuggestOpen(true);
                      }}
                      className="w-full bg-surface-container-low border-none rounded-xl pl-11 pr-10 rtl:pl-10 rtl:pr-11 py-3 text-sm font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all h-12 shadow-xs"
                      required={!formData.programId}
                    />
                    {programSearchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setProgramSearchQuery('');
                          setIsProgramSuggestOpen(true);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-on-surface-variant hover:text-primary rounded-full hover:bg-surface-container-high transition-colors rtl:right-auto rtl:left-3"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Auto-suggest dropdown popup */}
                  {isProgramSuggestOpen && (
                    <div className="absolute left-0 right-0 top-full mt-2 bg-surface-container-lowest rounded-2xl shadow-level4 border border-on-surface/5 overflow-hidden z-50 animate-fadeIn max-h-64 overflow-y-auto">
                      <div className="px-4 py-2 bg-surface-container-low/60 border-b border-on-surface/5 flex items-center justify-between text-[11px] font-label font-bold text-on-surface-variant">
                        <span>Daftar Program Tersedia</span>
                        <span className="opacity-60">{filteredPrograms.length} program</span>
                      </div>

                      {filteredPrograms.length === 0 ? (
                        <div className="p-4 text-center text-xs text-on-surface-variant font-body">
                          <Bilingual {...copy.createEvaluation.noProgramFound} />
                        </div>
                      ) : (
                        <div className="divide-y divide-on-surface/5">
                          {filteredPrograms.map(program => (
                            <div
                              key={program.id}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, programId: program.id }));
                                setProgramSearchQuery('');
                                setIsProgramSuggestOpen(false);
                              }}
                              className="p-3.5 hover:bg-surface-container-high transition-colors cursor-pointer flex items-center justify-between gap-3 group"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-headline font-bold text-xs sm:text-sm text-on-surface group-hover:text-primary transition-colors truncate">
                                    {program.name}
                                  </span>
                                  {program.code && (
                                    <span className="px-2 py-0.5 rounded bg-primary/10 text-primary font-mono text-[10px] font-bold shrink-0">
                                      {program.code}
                                    </span>
                                  )}
                                </div>
                                {program.description && (
                                  <p className="text-[11px] text-on-surface-variant m-0 truncate mt-0.5">
                                    {program.description}
                                  </p>
                                )}
                              </div>
                              <Check
                                size={16}
                                className="text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {isLoadingPrograms && (
                <p className="text-[10px] text-on-surface-variant animate-pulse m-0 pl-1">
                  <Bilingual {...copy.createEvaluation.syncingProgram} />
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.createEvaluation.labelDesc} />
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface placeholder:text-on-surface-variant/50 border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-none"
                placeholder={
                  language === 'ar'
                    ? copy.createEvaluation.placeholderDesc.ar
                    : copy.createEvaluation.placeholderDesc.id
                }
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
              <div className="space-y-2">
                <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.createEvaluation.labelType} />
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none appearance-none h-12"
                >
                  <option value="QUIZ">
                    {language === 'ar'
                      ? copy.createEvaluation.optionQuiz.ar
                      : copy.createEvaluation.optionQuiz.id}
                  </option>
                  <option value="EXAM">
                    {language === 'ar'
                      ? copy.createEvaluation.optionExam.ar
                      : copy.createEvaluation.optionExam.id}
                  </option>
                  <option value="SURVEY">
                    {language === 'ar'
                      ? copy.createEvaluation.optionSurvey.ar
                      : copy.createEvaluation.optionSurvey.id}
                  </option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.createEvaluation.labelPassingScore} />
                </label>
                <Input
                  type="number"
                  name="passingScore"
                  min="0"
                  max="100"
                  value={formData.passingScore}
                  onChange={handleChange}
                  className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
                  required
                />
              </div>
            </div>

            {/* KKM Context & Stage Relationship Info Note */}
            <div className="p-3.5 rounded-2xl bg-surface-container-low/60 border border-outline-variant/10 flex items-start gap-2.5 text-xs text-on-surface-variant font-body">
              <Info size={16} className="text-primary shrink-0 mt-0.5" />
              <p className="m-0 leading-relaxed">
                <Bilingual {...copy.createEvaluation.kkmNote} />
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.createEvaluation.labelAttempts} />
                </label>
                <Input
                  type="number"
                  name="maxAttempts"
                  min="0"
                  value={formData.maxAttempts}
                  onChange={handleChange}
                  placeholder={
                    language === 'ar'
                      ? copy.createEvaluation.descAttempts.ar
                      : copy.createEvaluation.descAttempts.id
                  }
                  className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
                  required
                />
                <p className="text-[10px] text-on-surface-variant opacity-60">
                  <Bilingual {...copy.createEvaluation.descAttempts} />
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.createEvaluation.labelTimeLimit} />
                </label>
                <Input
                  type="number"
                  name="timeLimit"
                  min="0"
                  value={formData.timeLimit}
                  onChange={handleChange}
                  placeholder={
                    language === 'ar'
                      ? copy.createEvaluation.descTimeLimit.ar
                      : copy.createEvaluation.descTimeLimit.id
                  }
                  className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
                />
                <p className="text-[10px] text-on-surface-variant opacity-60">
                  <Bilingual {...copy.createEvaluation.descTimeLimit} />
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.createEvaluation.labelRandomPool} />
              </label>
              <Input
                type="number"
                name="numberOfQuestions"
                min="1"
                value={formData.numberOfQuestions}
                onChange={handleChange}
                placeholder={t(copy.createEvaluation.placeholderRandomPool)}
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
              />
              <p className="text-[10px] text-on-surface-variant opacity-60">
                <Bilingual {...copy.createEvaluation.descRandomPool} />
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-8 border-t border-on-surface/5">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/evaluations')}
              className="px-8 border-outline-variant text-on-surface-variant uppercase text-[10px] font-bold tracking-widest"
              disabled={isPending}
            >
              <Bilingual {...copy.common.cancel} />
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="px-10 flex items-center gap-3 bg-primary text-on-primary shadow-level2 uppercase text-[10px] font-bold tracking-widest"
              disabled={isPending}
            >
              {isPending ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              <Bilingual {...copy.createEvaluation.btnSubmit} />
            </Button>
          </div>
        </form>
      </Card>

      <div className="bg-surface-container-high/20 p-6 rounded-2xl border border-on-surface/5 flex items-start gap-4">
        <ClipboardCheck size={20} className="text-secondary mt-1" />
        <div>
          <h4 className="text-sm font-headline font-bold text-on-surface">
            <Bilingual {...copy.createEvaluation.cardInfoTitle} />
          </h4>
          <p className="text-xs font-body text-on-surface-variant leading-relaxed">
            <Bilingual {...copy.createEvaluation.cardInfoDesc} />
          </p>
        </div>
      </div>
    </div>
  );
}
